import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  selectScheduleClassIdToObject,
  selectScheduleSelectedMatchIds,
  selectUnscheduledMatches,
} from '../../../../../features/schedule/scheduleSelectors';
import { MIN_SCHEDULE_BULK_ADJUST } from '../../../../../features/schedule/scheduleSelectionConstants';
import { useAppSelector } from '../../../../../hooks/redux';
import { blurScheduleToolbarFilters } from './scheduleLayoutHooks';
import { MatchItem } from '../DnDSchedule/Item';
import { IMatchScheduleItem } from '../../../../../types/schedule';

export type UnscheduledQuickFilter = 'all' | 'group' | 'knockout' | 'tbd';

type VirtualRow =
  | { kind: 'header'; key: string; classId: string; title: string; count: number }
  | { kind: 'match'; key: string; match: IMatchScheduleItem };

const sortMatchesInClass = (matches: IMatchScheduleItem[]) => {
  return [...matches].sort((a, b) => {
    const aIsGroup = !!a.tournamentGroup;
    const bIsGroup = !!b.tournamentGroup;
    const aKey = String(a.sortKey || '');
    const bKey = String(b.sortKey || '');

    if (aIsGroup && !bIsGroup) return -1;
    if (!aIsGroup && bIsGroup) return 1;

    if (aIsGroup && bIsGroup) {
      if (a.tournamentGroup!.id !== b.tournamentGroup!.id) {
        return a.tournamentGroup!.id - b.tournamentGroup!.id;
      }
      return aKey.localeCompare(bKey, undefined, { numeric: true });
    }

    const aType = typeof a.type === 'number' ? a.type : 999;
    const bType = typeof b.type === 'number' ? b.type : 999;

    if (aType !== bType) {
      return aType - bType;
    }

    return aKey.localeCompare(bKey, undefined, { numeric: true });
  });
};

const isTbdMatch = (m: IMatchScheduleItem) => {
  const homeName = (m.homeTeam?.name || m.homeTeamPlaceholder || '').trim();
  const awayName = (m.awayTeam?.name || m.awayTeamPlaceholder || '').trim();
  return (
    homeName === '' ||
    awayName === '' ||
    homeName.toUpperCase() === 'TBD' ||
    awayName.toUpperCase() === 'TBD'
  );
};

const HEADER_ROW_HEIGHT = 44;
const MATCH_ROW_HEIGHT = 118;

export type UnscheduledMatchesProps = {
  pendingPlaceMatchId: number | null;
  /** Which UI source owns the pending placement highlight (unscheduled list vs grid). */
  pendingPlacementSource: 'unscheduled' | 'scheduled_grid' | null;
  onSelectPendingMatch: (matchId: number | null) => void;
  onClearPendingFromDrag: () => void;
  isMobile: boolean;
  scheduleMutationBlocked?: boolean;
  dragModifierBlocked?: boolean;
};

export const UnscheduledMatches = ({
  pendingPlaceMatchId,
  pendingPlacementSource,
  onSelectPendingMatch,
  onClearPendingFromDrag,
  isMobile,
  scheduleMutationBlocked = false,
  dragModifierBlocked = false,
}: UnscheduledMatchesProps) => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const unscheduledMatches = useAppSelector(selectUnscheduledMatches);
  const selectedMatchIds = useAppSelector(selectScheduleSelectedMatchIds);
  const classIdToObject = useAppSelector(selectScheduleClassIdToObject);

  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState<UnscheduledQuickFilter>('all');
  const [collapsedClasses, setCollapsedClasses] = useState<Record<string, boolean>>({});
  const [mobileExpanded, setMobileExpanded] = useState(true);

  const scrollParentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMobile && pendingPlaceMatchId != null) {
      setMobileExpanded(false);
    }
  }, [isMobile, pendingPlaceMatchId]);

  const filteredMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return unscheduledMatches.filter((m) => {
      if (quickFilter === 'group' && !m.tournamentGroup) return false;
      if (quickFilter === 'knockout' && m.tournamentGroup) return false;
      if (quickFilter === 'tbd' && !isTbdMatch(m)) return false;

      if (!q) return true;

      const className = (classIdToObject[m.classId]?.name || '').toLowerCase();
      const groupName = (m.tournamentGroup?.name || '').toLowerCase();
      const home = (m.homeTeam?.name || m.homeTeamPlaceholder || '').toLowerCase();
      const away = (m.awayTeam?.name || m.awayTeamPlaceholder || '').toLowerCase();
      const mid = (m.matchId || '').toLowerCase();
      const sk = String(m.sortKey ?? '');

      return (
        className.includes(q) ||
        groupName.includes(q) ||
        home.includes(q) ||
        away.includes(q) ||
        mid.includes(q) ||
        sk.includes(q)
      );
    });
  }, [unscheduledMatches, search, quickFilter, classIdToObject]);

  const distinctClassCount = useMemo(
    () => new Set(filteredMatches.map((m) => m.classId)).size,
    [filteredMatches]
  );
  const defaultCollapsedByVolume = distinctClassCount > 3;

  const rows: VirtualRow[] = useMemo(() => {
    const grouped = filteredMatches.reduce((acc, match) => {
      const classId = String(match.classId);
      if (!acc[classId]) acc[classId] = [];
      acc[classId].push(match);
      return acc;
    }, {} as Record<string, IMatchScheduleItem[]>);

    const classIds = Object.keys(grouped).sort((a, b) => {
      const nameA = classIdToObject[a]?.name || '';
      const nameB = classIdToObject[b]?.name || '';
      return nameA.localeCompare(nameB);
    });

    const out: VirtualRow[] = [];
    for (const classId of classIds) {
      const title = classIdToObject[classId]?.name || 'Unknown Class';
      const matches = sortMatchesInClass(grouped[classId]);
      const isCollapsed = collapsedClasses[classId] ?? defaultCollapsedByVolume;

      out.push({
        kind: 'header',
        key: `h-${classId}`,
        classId,
        title,
        count: matches.length,
      });

      if (!isCollapsed) {
        for (const m of matches) {
          out.push({ kind: 'match', key: `m-${m.id}`, match: m });
        }
      }
    }
    return out;
  }, [filteredMatches, classIdToObject, collapsedClasses, defaultCollapsedByVolume]);

  const toggleCollapse = useCallback(
    (classId: string) => {
      setCollapsedClasses((prev) => {
        const current = prev[classId] ?? defaultCollapsedByVolume;
        return { ...prev, [classId]: !current };
      });
    },
    [defaultCollapsedByVolume]
  );

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: (index) => (rows[index]?.kind === 'header' ? HEADER_ROW_HEIGHT : MATCH_ROW_HEIGHT),
    overscan: 12,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const handleSelectMatch = useCallback(
    (id: number) => {
      if (scheduleMutationBlocked) return;
      onSelectPendingMatch(id);
    },
    [scheduleMutationBlocked, onSelectPendingMatch]
  );

  useEffect(() => {
    rowVirtualizer.measure();
  }, [rows, rowVirtualizer]);

  const panelBody = (
    <>
      <div className="mb-2">
        <label className="form-label small mb-1" htmlFor="unscheduled-search">
          {t(prefix + 'scheduleUnscheduledSearchLabel')}
        </label>
        <input
          id="unscheduled-search"
          type="search"
          className="form-control form-control-sm"
          placeholder={t(prefix + 'scheduleUnscheduledSearchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t(prefix + 'scheduleUnscheduledSearchLabel')}
        />
      </div>

      <p className="small text-muted mb-2 mb-md-3">{t(prefix + 'scheduleUnscheduledDoubleClickHint')}</p>

      <div
        className="btn-group btn-group-sm w-100 mb-2"
        role="group"
        aria-label={t(prefix + 'scheduleUnscheduledQuickFiltersAria')}
      >
        {(['all', 'group', 'knockout', 'tbd'] as UnscheduledQuickFilter[]).map((f) => (
          <button
            key={f}
            type="button"
            className={`btn ${quickFilter === f ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setQuickFilter(f)}
          >
            {f === 'all' && t(prefix + 'scheduleUnscheduledFilterAll')}
            {f === 'group' && t(prefix + 'scheduleUnscheduledFilterGroup')}
            {f === 'knockout' && t(prefix + 'scheduleUnscheduledFilterKnockout')}
            {f === 'tbd' && t(prefix + 'scheduleUnscheduledFilterTbd')}
          </button>
        ))}
      </div>

      <div
        ref={scrollParentRef}
        className="unscheduled-matches-scroll flex-grow-1 min-h-0"
        role="listbox"
        aria-label={t(prefix + 'unscheduledMatchesTitle')}
        onPointerDownCapture={() => blurScheduleToolbarFilters()}
      >
        {rows.length === 0 ? (
          <div className="text-muted text-center py-3 small">
            {t(prefix + 'scheduleUnscheduledNoMatches')}
          </div>
        ) : (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualItems.map((virtualRow) => {
              const row = rows[virtualRow.index];
              if (!row) return null;

              if (row.kind === 'header') {
                const isCollapsed = collapsedClasses[row.classId] ?? defaultCollapsedByVolume;

                return (
                  <div
                    key={row.key}
                    className="border rounded px-2 py-1 bg-white d-flex justify-content-between align-items-center user-select-none"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none text-dark text-start p-0 flex-grow-1"
                      onClick={() => toggleCollapse(row.classId)}
                    >
                      <span className="fw-semibold small">{row.title}</span>
                      <span className="badge bg-secondary ms-2">{row.count}</span>
                    </button>
                    <span className="text-muted small">{isCollapsed ? '▼' : '▲'}</span>
                  </div>
                );
              }

              const m = row.match;
              const pending =
                pendingPlaceMatchId === m.id && pendingPlacementSource === 'unscheduled';

              return (
                <div
                  key={row.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <div className="border rounded p-1 mb-1 bg-white shadow-sm">
                    <MatchItem
                      item={m}
                      id={m.id.toString()}
                      isScheduled={false}
                      scale={5}
                      width={268}
                      classObject={classIdToObject[m.classId]}
                      onSelectForPlacement={handleSelectMatch}
                      onUnscheduledDragStarted={onClearPendingFromDrag}
                      placementPressed={pending}
                      placementDragDisabled={pendingPlaceMatchId != null}
                      dragModifierBlocked={dragModifierBlocked}
                      scheduleMutationBlocked={scheduleMutationBlocked}
                      bulkSelected={
                        selectedMatchIds.length >= MIN_SCHEDULE_BULK_ADJUST &&
                        selectedMatchIds.includes(m.id)
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  if (unscheduledMatches.length === 0) {
    return (
      <div className="border rounded p-3 bg-light small text-muted text-center">
        {t(prefix + 'scheduleUnscheduledEmpty')}
      </div>
    );
  }

  const collapsedBar = (
    <button
      type="button"
      className="btn btn-light w-100 py-2 border-0 rounded-0"
      onClick={() => setMobileExpanded(true)}
      aria-expanded={mobileExpanded}
    >
      {t(prefix + 'scheduleUnscheduledDrawerCollapsed', { count: unscheduledMatches.length })}
    </button>
  );

  return (
    <section
      className={`unscheduled-matches-panel d-flex flex-column flex-grow-1 min-h-0 ${
        isMobile ? 'schedule-workbench__unscheduled--mobile-drawer' : ''
      }`}
      aria-label={t(prefix + 'unscheduledMatchesTitle')}
    >
      {isMobile && !mobileExpanded ? (
        collapsedBar
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h3 className="h6 mb-0">{t(prefix + 'unscheduledMatchesTitle')}</h3>
            <span className="badge bg-secondary">{unscheduledMatches.length}</span>
          </div>
          {isMobile && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mb-2"
              onClick={() => setMobileExpanded(false)}
            >
              {t(prefix + 'scheduleUnscheduledMinimize')}
            </button>
          )}
          {panelBody}
        </>
      )}
    </section>
  );
};
